import { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CHANNEL_NAMES, connectionLabel, useConnections, type MessagingChannel } from "./api";
import { ConnectionLine } from "./ConnectionLine";
import { LinkCodeBlock } from "./LinkCodeBlock";
import { DIALOG_CLOSE_TARGET, TAP_ON_PHONE, focusMainControl } from "./classes";

// While the dialog is open it watches for the code to arrive from the phone.
const POLL_MS = 3000;

/** What the dialog is doing, fixed when it opens. */
export interface LinkingState {
  /** The channel's link ids when the dialog opened: any other one is the new link. */
  known: number[];
  /** Adding a number beside the linked ones (a channel that takes several). */
  adding?: boolean;
  /** The link being switched away from (a channel that takes one). */
  replacing?: number;
}

/** Link a channel, add a number to it or switch its number: Linked once a new link shows up. */
export function LinkChannelDialog({
  channel,
  open,
  onOpenChange,
  linking,
  onCloseAutoFocus,
}: {
  channel: MessagingChannel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linking: LinkingState;
  /** Where focus goes when the dialog closes (Radix returns it to the opener by default). */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const connections = useConnections({ pollMs: open ? POLL_MS : undefined });
  const rows = connections.data?.connections ?? [];
  const linked = rows.find((row) => row.channel === channel && !linking.known.includes(row.id));
  const name = CHANNEL_NAMES[channel];
  const title = linking.replacing
    ? `Switch ${name} number`
    : linking.adding
      ? `Add ${name} number`
      : `Link ${name}`;
  // When the code arrives the dialog flips to Linked: the code block goes, so focus moves to Done.
  const done = useRef<HTMLButtonElement>(null);
  const linkedId = linked?.id;
  useEffect(() => {
    if (open && linkedId !== undefined) done.current?.focus();
  }, [open, linkedId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-sm", DIALOG_CLOSE_TARGET)}
        aria-describedby={undefined}
        onOpenAutoFocus={focusMainControl}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {linked ? (
          <div className="flex flex-col gap-4">
            <ConnectionLine channel={channel} label={connectionLabel(linked)} linked />
            <Button ref={done} className={TAP_ON_PHONE} onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        ) : (
          open && (
            <div className="flex flex-col gap-4">
              <LinkCodeBlock channel={channel} />
            </div>
          )
        )}
        <p role="status" className="sr-only">
          {linked ? `${name} linked` : ""}
        </p>
      </DialogContent>
    </Dialog>
  );
}
